"use client";

import { type CSSProperties, type ReactNode } from "react";

/**
 * LND — THE landing page concept, one version, developed in place. The
 * working page won the four-way comparison on 2026-08-25; palette variants
 * live on the standalone viewer (/concept/landing/lnd-001?palette=…).
 *
 * Composition rules learned in review: the proven full-surface exhibits
 * render in plate mode (chrome off) so the page's own band voice does the
 * talking; product shots stay light; the night ink is reserved for the claret
 * close and the UK infrastructure panel; everything reads inside a 78rem rail.
 * The current composition has six bands: how the Twin is built (PRC-009),
 * the continuous Twin story (MOT-002), the governed answer (SES-001), any
 * model and every door (GOV-007), hosting and the UK
 * research direction (in progress, links to /research#uk), and how you start
 * (PRC-002); the
 * workspace exhibits (CHV-001/006/007, HRO-011) and the receipt sheet
 * (DRW-003) stay on the board, and FAQ-001 answers buyer questions on this page.
 *
 * Copy discipline: what the Twin builds — definitions, links, actions — are
 * proposals a person accepts, declines, puts aside for later or undoes
 * (twin proposals, 2026-09-04). Use those verbs; never write "approve".
 *
 * The group is NOT rendered on the board's main scroll — the gallery
 * filters it; /concept/landing is the only place the page renders.
 */
import { EDITORIAL_CONTAINER_CLS, FaqSection, LABEL_CLS } from "@/components/editorial";
import { FAQS } from "@/lib/faqs";
import { LogoStrip, WorkingPageHero } from "@/components/editorial/landing-hero";
import { Circled, SketchCallout, Underlined } from "@/components/sketch";
import { HOME_COPY } from "@/lib/landing-copy";
export { WorkingPageHero } from "@/components/editorial/landing-hero";

import type { ConceptGroup } from "../spec";
import { ModelRouteCard } from "./governance";
import { ScrollStory } from "../twin-story/scroll-story";
import { HostingInfrastructure } from "./landing-infrastructure";
import { DualCtaBand } from "./cta";
import { BandRail } from "./pages";
import { ActivationTimeline, AuthoringChain } from "./process";
import { GovernedAnswerExhibit } from "./sessions";

const INK_58 = "text-[color-mix(in_srgb,var(--theme-ink)_58%,transparent)]";

/* ================================================================= */
/*  Page furniture                                                    */
/* ================================================================= */

function PageShell({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <div className={EDITORIAL_CONTAINER_CLS}>
      {/* One reading column. The page lives inside a 78rem rail — the board
          sheet's proportion — so exhibits stay composed on wide viewports
          instead of stretching to the window. */}
      <div className="mx-auto w-full max-w-[78rem]">
        {label ? <p className={`${INK_58} ${LABEL_CLS} pt-10`}>{label}</p> : null}
        {children}
      </div>
    </div>
  );
}

/** Stacked band: rail header, then the exhibit at full width. An optional
 *  hand-drawn note sits beside the header from 1024px. */
function Band({
  head,
  lede,
  note,
  children,
  id,
}: {
  head: ReactNode;
  lede: string;
  note?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  const rail = (
    <BandRail head={head} headingLevel="h2">
      {lede}
    </BandRail>
  );
  return (
    <section id={id} className="scroll-mt-24 border-b border-dotted border-[var(--rule)] py-12">
      {note ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,38rem)_minmax(0,1fr)] lg:items-end">
          {rail}
          <div className="hidden justify-self-center lg:block">{note}</div>
        </div>
      ) : (
        rail
      )}
      <div className="mt-7 min-w-0">{children}</div>
    </section>
  );
}

/**
 * "An answer you can check.", with the verb circled and a pointer from the
 * header into the governed answer (SKT-003, shipped 2026-09-25).
 */
export function CheckableAnswerBand() {
  return (
    <Band
      head={
        <>
          An answer you can <Circled className="mx-[0.22em]">check</Circled>.
        </>
      }
      lede="See who owes what, using the same definition of outstanding debt. Inspect the invoices, ageing calculation and sources behind the answer."
      note={<SketchCallout note={"the sources are\nright here"} arrow="down-left" />}
    >
      <GovernedAnswerExhibit chrome={false} />
    </Band>
  );
}

const CLOSING_MARK = "keeps chasing";

/**
 * The dark close with "keeps chasing" underlined in the light accent, so the
 * pen reads on the night surface (SKT-006, shipped 2026-09-25). The words
 * stay in HOME_COPY; the mark fails loudly if they change.
 */
export function ClosingBand() {
  const [before, after] = HOME_COPY.closing.split(CLOSING_MARK);
  if (after === undefined) {
    throw new Error(`HOME_COPY.closing no longer contains "${CLOSING_MARK}"; update ClosingBand's mark.`);
  }
  return (
    <section className="my-12" style={{ "--sketch-ink-on": "var(--theme-accent)" } as CSSProperties}>
      <DualCtaBand
        title={
          <>
            {before}
            <span className="whitespace-nowrap">
              <Underlined>{CLOSING_MARK}</Underlined>
              {after}
            </span>
          </>
        }
      />
    </section>
  );
}

/** Band for an exhibit that carries its own heading. */
function ExhibitBand({
  children,
  id,
}: {
  children: ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className="scroll-mt-24 border-b border-dotted border-[var(--rule)] py-12">
      <div className="min-w-0">{children}</div>
    </section>
  );
}

/* ================================================================= */
/*  The working page                                                  */
/* ================================================================= */

export function WorkingPage({ production = false }: { production?: boolean } = {}) {
  return (
    <PageShell label={production ? undefined : "MLX · the landing page, working concept"}>
      <WorkingPageHero production={production} />
      <LogoStrip />

      <Band
        id="how-it-works"
        head="Agree the meaning. Reuse the understanding."
        lede="Which records describe the same customer? What counts as overdue? Your Twin connects identities, relationships and definitions, then checks the calculations against your data."
      >
        <AuthoringChain />
      </Band>

      <ScrollStory landing />

      <CheckableAnswerBand />

      <Band
        head="Use your Twin where you work."
        lede="The same records, definitions and relationships stay in your Twin as your AI tools change. Each connection has its own setup and access controls."
      >
        <ModelRouteCard chrome={false} doors />
      </Band>

      <Band
        id="infrastructure"
        head="Your Twin. Your infrastructure."
        lede="A dedicated server for your business, with control over where it runs."
      >
        <HostingInfrastructure />
      </Band>

      <ExhibitBand>
        <ActivationTimeline chrome={false} />
      </ExhibitBand>

      <FaqSection
        contained
        title="What you control, and how it works."
        intro="Where your data goes, what it costs and what you control."
        items={FAQS}
      />

      {/* CTA-006's dual band as the footer hero: activation and
          partnership, on the editorial night. */}
      <ClosingBand />

      {production ? null : (
      <p className={`${INK_58} pb-6 text-[0.72rem] leading-[1.5]`}>
        LND-001, the working page. The hero runs the live WebGL twin with its
        arrival layer and synced legend over the connector grid; then six
        bands: PRC-009&rsquo;s authoring chain, MOT-002&rsquo;s continuous Twin story,
        SES-001&rsquo;s governed answer in plate mode, GOV-007&rsquo;s
        model-swap-and-doors card, the flag region chooser and PRC-002&rsquo;s
        activation timeline; FAQ-001 answers buyer questions inline and CTA-006&rsquo;s
        dual band closes the page. Palette variants live on this viewer.
      </p>
      )}
    </PageShell>
  );
}

/* ================================================================= */

export const LANDING_GROUP: ConceptGroup = {
  id: "landing-pages",
  prefix: "LND",
  title: "Landing pages",
  blurb:
    "The landing page concept, one version, developed in place: instrument hero over the connector grid, then six bands under slim numbered kickers, PRC-009's authoring chain, MOT-002's continuous Twin story, SES-001's governed answer in plate mode, GOV-007's model-swap-and-doors card, the hosting section with its UK research panel and PRC-002's activation timeline, with FAQ-001's live accordion and CTA-006's dual band as the close. Light product shots; a 78rem rail; about 1,300 words. Not rendered on the board scroll, view it full-page at /concept/landing/lnd-001, where palette variants live.",
  specs: [
    {
      id: "LND-001",
      title: "The working page",
      note: "The landing page, one version: instrument hero with arrival layer, synced legend and connector grid; the authoring chain; the continuous Twin story; SES-001's governed answer; the model-swap-and-doors card; the hosting section with its UK research panel; PRC-002's activation timeline; FAQ-001's live accordion; CTA-006's dual close. Palette variants on the full-page viewer.",
      surface: "ground",
      width: "full",
      interaction: "prototype",
      state: "One scroll · the version",
      href: "/concept/landing/lnd-001",
      render: () => <WorkingPage />,
    },
  ],
};
